<template>
    <v-container fluid class="editor-layout">
        <v-row no-gutters>

            <v-btn icon @click="drawer = !drawer" class="toggle-btn">
                <v-icon>{{ drawer ? 'mdi-chevron-left' : 'mdi-menu' }}</v-icon>
            </v-btn>                

            <!-- Toggle Button -->
            <v-navigation-drawer v-model="drawer"  class="sidebar-drawer">

                <h3>Steps</h3>
                <div
                    v-for="group in groupedSidebarNodes"
                    :key="group.parent.key"
                    class="drag-drawflow"
                    draggable="true"
                    @dragstart="drag($event)"
                    :data-node="group.parent.key"
                >
                    <v-icon>mdi-code-braces</v-icon
                    ><span>{{ group.parent.title }}</span>
                    <div
                        v-for="child in group.children"
                        :key="child.key"
                        class="child-node"
                    >
                        <v-icon>mdi-code-braces</v-icon>
                        <span>{{ child.title }}</span>
                    </div>
                </div>

                <div
                    v-for="node in sidebarNodes"
                    :key="node.key"
                    class="drag-drawflow"
                    draggable="true"
                    @dragstart="drag($event)"
                    :data-node="node.key"
                >
                    <v-icon>mdi-code-braces</v-icon>
                    <span>{{ node.title }}</span>
                </div>
            </v-navigation-drawer>
            <v-col  class="editor-content">
                <div
                    id="drawflow"
                    @drop="drop($event)"
                    @dragover="allowDrop($event)"
                >
                    <v-btn
                        style="z-index: 9999"
                        class="primary-btn"
                        @click="saveAllJourneySteps"
                        >Save</v-btn
                    >
                    <!-- <v-btn style="z-index: 9999" color="primary" @click="exportData">Export</v-btn> -->
                    <v-btn
                        style="z-index: 9999"
                        class="primary-btn"
                        @click.stop="showDrawflowLog"
                        >Show Logs</v-btn
                    >
                    <v-btn
                        style="z-index: 9999"
                        class="danger-btn"
                        @click="editor.clearModuleSelected()"
                        >Clear</v-btn
                    >
                    <v-btn icon @click="toggleLock">
                        <v-icon v-if="isLocked">mdi-lock</v-icon>
                        <v-icon v-else>mdi-lock-open</v-icon>
                    </v-btn>
                    <v-btn-toggle>
                        <v-btn
                            style="z-index: 9999"
                            icon
                            @click="editor.zoom_out()"
                        >
                            <v-icon>mdi-magnify-minus</v-icon>
                        </v-btn>
                        <v-btn
                            style="z-index: 9999"
                            icon
                            @click="editor.zoom_reset()"
                        >
                            <v-icon>mdi-magnify</v-icon>
                        </v-btn>
                        <v-btn
                            style="z-index: 9999"
                            icon
                            @click="editor.zoom_in()"
                        >
                            <v-icon>mdi-magnify-plus</v-icon>
                        </v-btn>
                    </v-btn-toggle>
                </div>
            </v-col>
        </v-row>
        <connection-condition-dialog
            v-model="connectionConditionDialog.data"
            :show.sync="connectionConditionDialog.show"
            :title="connectionConditionDialog.title"
            @save="saveTransitionConditions"
        />
        <logs-dialog
            :show.sync="showDrawflowLogDialog"
            :logs="drawflowLogsHtml"
        ></logs-dialog>
    </v-container>
</template>

<script>
import Vue from "vue";
import { mapActions, mapGetters, mapMutations } from "vuex";
import DrawflowExtended from "./DrawFlowExtended.js";
import "drawflow/dist/drawflow.min.css";
import JourneyStepNode from "./JourneyStepNode.vue";
import ConnectionConditionDialog from "./ConnectionConditionDialog.vue";
import WaitingDialog from "../Dialogs/WaitingDialog.vue";
import ConfirmDialog from "../Dialogs/ConfirmDialog.vue";
import LogsDialog from "./LogsDialog.vue";

export default {
    components: {
        JourneyStepNode,
        ConnectionConditionDialog,
        WaitingDialog,
        ConfirmDialog,
        LogsDialog,
    },
    data: () => ({
        editor: null,
        dragging: false,
        isLocked: false,
        /**
         * chart has been changed
         */
        isDirty: false,
        /**
         * current nodes in drawflow
         */
        nodesData: {},
        /**
         * store all nodes' data, only have basic info (key, name, parent, title, id, description)
         */
        sidebarNodesData: {},
        /**
         * current available node
         */
        sidebarNodes: [],
        /**
         * current available group node
         */
        groupedSidebarNodes: [],
        lastPosition: { x: 100, y: 100 },
        /**
         * nodes that have already added into drawflow
         */
        addedNodes: new Set(),
        connectionConditionDialog: {
            show: false,
            data: {},
            title: "",
        },
        outputNodeKey: null,
        drawflowLogs: [],
        showDrawflowLogDialog: false,
        maxOrder: 0, // Global maximum order tracker
        parentProcessed: new Set(),
        childrenProcessed: new Set(),

        drawer: true, // Controls sidebar visibility
    }),
    computed: {
        ...mapGetters("journey", {
            journeySteps: "currentJourneySteps",
            journeyStep: "currentJourneyStep",
            steps: "steps",
        }),
        outputJourneyStep() {
            return this.outputNodeKey
                ? this.journeyStep(this.outputNodeKey)
                : null;
        },
        drawflowLogsHtml() {
            let listItems = this.drawflowLogs
                .map((item) => `<li>${item}</li>`)
                .join("");
            return `<ul>${listItems}</ul>`;
        },
    },
    methods: {
        ...mapActions("journey", [
            "saveJourneySteps",
            "saveJourneyStep",
            "deleteJourneyStep",
        ]),
        ...mapMutations("journey", [
            "addCurrentJourneySteps",
            "setNeedToRefreshCanvas",
        ]),
        initializeEditor() {
            const id = document.getElementById("drawflow");
            this.editor = new DrawflowExtended(id, Vue, this);
            this.editor.start();
            this.editor.reroute = true;

            // register node
            this.editor.registerNode("stepnode", JourneyStepNode);

            // Add nodeMoved event listener
            this.editor.on("nodeMoved", this.handleNodeMoved);
            this.editor.on("nodeRemoved", this.handleNodeRemoved);
            this.editor.on("connectionCreated", this.handleConnectionCreated);
        },
        handleNodeMoved(id) {
            this.isDirty = true;
            const node = this.editor.getNodeFromId(id);
            // update vuex
            const step = this.journeyStep(node.name);
            const newStep = structuredClone(step);
            newStep["pos_x"] = node.pos_x;
            newStep["pos_y"] = node.pos_y;
            this.addCurrentJourneySteps({ key: newStep.key, step: newStep });
            this.addToDrawflowLog(
                `Move node-${id}, step-${step.key} to x = ${node.pos_x}, y = ${node.pos_y}`,
                "info",
            );
        },
        async handleNodeRemoved(node) {
            if (!(await this.isRemoveNode(node))) {
                // add back
                const data = JSON.parse(node.data.nodedata);
                this.addNodeToDrawFlow(data, node.pos_x, node.pos_y);
                setTimeout(() => {
                    this.createAllConnections();
                }, 5000);
                return;
            }
            this.$modal.show(WaitingDialog, {
                title: "Removing step from journey...",
                message: "Please wait whilst the step is being removing",
            });
            // 1. remove node from vuex
            try {
                const journeyStep = this.journeyStep(node.name);
                await this.deleteJourneyStep(journeyStep);
            } catch (error) {
                // add node back to draw flow
                const data = JSON.parse(node.data.nodedata);
                this.addNodeToDrawFlow(data, node.pos_x, node.pos_y);
                this.createAllConnections();
                this.$modal.submit();
                this.$modal.show(ConfirmDialog, {
                    title: "Failed",
                    message: error,
                    confirmText: "Confirm",
                });
                return;
            }

            // get input conntection node id
            const inputNodeIds = node["inputs"]["input_1"]["connections"].map(
                (node) => node.node,
            );
            console.log(inputNodeIds);
            inputNodeIds.forEach((id) => {
                const journeyStepNode = this.editor.getNodeFromId(id);
                // remove connection from journey step (connection is in condition)
                this.removeConnectionInJourneyStep(journeyStepNode, node.name);
            });

            // 2. add node back to side bar
            // remove from nodes data
            delete this.nodesData[node.name];

            // remove from addedNodes
            if (this.addedNodes.has(node.name)) {
                this.addedNodes.delete(node.name);
            }

            // add node back to side bar
            // get data from sidebar nodes data
            const sidebarNodeData = this.sidebarNodesData[node.name];
            this.sidebarNodes.push(sidebarNodeData);

            this.$modal.submit();
        },
        async isRemoveNode(node) {
            return this.$modal.show(ConfirmDialog, {
                title: "Delete Journey Step",
                message: `Journey Step ${node["name"]} will be deleted from Journey.`,
                confirmText: "Delete",
                cancelText: "Cancel",
                confirmButtonColor: "md-danger",
            });
        },
        removeConnectionInJourneyStep(node, removedNodeName) {
            let journeyStep = this.journeyStep(node.name);
            this.addToDrawflowLog(
                `remove ${removedNodeName} from ${node["name"]}`,
                "info",
            );
            journeyStep.conditions = journeyStep.conditions.filter(
                (condition) => condition.step !== removedNodeName,
            );
            this.saveJourneyStep(journeyStep);
        },
        handleConnectionCreated({ output_id, input_id }) {
            // check condition in output node
            // if input node is not in the condition of output node, pop the dialog to user to add condition
            // get output node
            const outputNode = this.editor.getNodeFromId(output_id);
            // get step detail from vuex
            const journeyStep = this.journeyStep(outputNode.name);

            // get input node
            const inputNode = this.editor.getNodeFromId(input_id);
            const inputNodeKey = inputNode.name;

            // check conditions for journeyStep (outputnode)
            const inputInNextStep = journeyStep.conditions.some(
                (condition) => condition.step === inputNodeKey,
            );
            if (!inputInNextStep) {
                // input node is not in the conditions of output node, show dialog
                this.outputNodeKey = outputNode.name;
                this.openConnectionConditionDialog(
                    outputNode.name,
                    inputNodeKey,
                );
            } else {
                // TODO remove the connection if user did not enter the condition
                console.log("Input Node is in the condition of output Node");
            }

            // Example: Determine new order based on connection (e.g., input follows output)
            const newOrder = this.journeyStep(outputNodeKey)?.order + 1 || 1;

            const shouldSave = this.updateOrderForNode(inputNodeKey, newOrder);

            if (shouldSave) {
                this.saveJourneyStep(this.journeyStep(inputNodeKey)); // Save only the updated node
            }
        },
        loadSideNodes() {
            const groupedNodes = {};
            // filter side node if the node's parent is in flow or the node is in flow
            const steps = this.steps.filter(
                (step) => !this.addedNodes.has(step["key"]),
            );
            for (let step of steps) {
                if (step.parent) {
                    if (!groupedNodes[step.parent]) {
                        groupedNodes[step.parent] = [];
                    }
                    groupedNodes[step.parent].push(step);
                } else {
                    this.sidebarNodes.push(step);
                }
            }
            for (const [parent, children] of Object.entries(groupedNodes)) {
                // get parent node
                const parentNode = this.steps.find(
                    (step) => step.key == parent,
                );
                this.groupedSidebarNodes.push({ parent: parentNode, children });
                // remove parent node from side bar nodes
                this.sidebarNodes = this.sidebarNodes.filter(
                    (node) => node.key != parent,
                );
            }
        },
        loadSidebarNodesData() {
            this.addToDrawflowLog("Start add node data to sidebar...", "bold");
            const steps = this.steps;
            // const groupedNodes = {};
            const uniqueKeys = new Set();
            let uniqueId = 1; // A counter to help generate unique IDs

            for (let step of steps) {
                if (uniqueKeys.has(step.key)) {
                    this.addToDrawflowLog(
                        `Duplicate key detected: '${step.key}'. Generating a unique key.`,
                        "warning",
                    );
                    step.key = `${step.key}-${uniqueId++}`;
                }
                uniqueKeys.add(step.key);

                this.sidebarNodesData[step.key] = {
                    ...step,
                    conditions: [],
                    validation: [],
                    images: [],
                    answers: [],
                };
            }
        },
        loadNodesFromJson() {
            const steps = this.journeySteps;
            // const groupedNodes = {};
            const uniqueKeys = new Set();
            let uniqueId = 1; // A counter to help generate unique IDs

            for (let step of steps) {
                if (uniqueKeys.has(step.key)) {
                    this.addToDrawflowLog(
                        `Duplicate key detected: '${step.key}'. Generating a unique key.`,
                        "warning",
                    );
                    step.key = `${step.key}-${uniqueId++}`;
                }
                uniqueKeys.add(step.key);

                this.nodesData[step.key] = {
                    ...step,
                };
            }
        },
        addAllNodes() {
            let pos_x = this.lastPosition.x;
            const parentSpacing = 250; // Adjust horizontal spacing for parents as needed
            const childSpacing = 170; // Adjust vertical spacing for children as needed

            const nodesToBeAdded = []; // Array to hold nodes in the order they were loaded
            const parentLastChildPosY = {}; // Dictionary to track last Y position for each parent's children

            // Capture all parent nodes and non-parent nodes
            Object.entries(this.nodesData).forEach(([key, node]) => {
                if (!node.parent) {
                    nodesToBeAdded.push({ key, node, type: "parent" });
                } else {
                    nodesToBeAdded.push({ key, node, type: "child" });
                }
            });

            // Add all parent nodes and non-parent nodes first
            nodesToBeAdded.forEach(({ key, node, type }) => {
                if (type === "parent") {
                    let coor_x = node.pos_x ?? "";
                    if (coor_x === "") {
                        coor_x = pos_x;
                    }
                    let coor_y = node.pos_y ?? "";
                    if (coor_y === "") {
                        coor_y = this.lastPosition.y;
                    }
                    this.addNodeToDrawFlow(node, coor_x, coor_y);
                    // update vuex with position
                    this.addCurrentJourneySteps({
                        key,
                        step: { ...node, pos_x: coor_x, pos_y: coor_y },
                    });
                    // Store parent position in the node data
                    this.nodesData[key].pos_x = coor_x;
                    this.nodesData[key].pos_y = coor_y;
                    // Initialize the last child position for this parent
                    parentLastChildPosY[key] = coor_y;
                    pos_x += parentSpacing;
                }
            });

            // Add all child nodes under their respective parent nodes
            nodesToBeAdded.forEach(({ key, node, type }) => {
                if (type === "child") {
                    let parentNodeData = this.nodesData[node.parent];
                    if (parentNodeData) {
                        // Determine the position for the child node
                        let coor_x = node.pos_x ?? "";
                        if (coor_x === "") {
                            coor_x = parentNodeData.pos_x;
                        }
                        let coor_y = node.pos_y ?? "";
                        if (coor_y === "") {
                            coor_y =
                                parentLastChildPosY[node.parent] + childSpacing;
                        }

                        // Add the child node
                        this.addNodeToDrawFlow(node, coor_x, coor_y);

                        // update vuex with position
                        this.addCurrentJourneySteps({
                            key,
                            step: { ...node, pos_x: coor_x, pos_y: coor_y },
                        });

                        // Update the last child position for the parent
                        parentLastChildPosY[node.parent] = coor_y;

                        // Update the position of the last added child node
                        this.nodesData[key].pos_x = coor_x;
                        this.nodesData[key].pos_y = coor_y;
                    } else {
                        this.addToDrawflowLog(
                            `Parent node ${node.parent} not found for child node ${key}`,
                            "warning",
                        );
                    }
                }
            });

            //this.lastPosition.x = posX; // Update last position for any additional nodes
        },
        /**
         * add node into draw
         * @param {object} node node data, should include, name, description, key and parent
         * @param {int} pos_x
         * @param {int} pos_y
         */
        addNodeToDrawFlow(node, pos_x, pos_y) {
            // add node to draw
            const { title, description, key } = node;
            const nodedata = JSON.stringify({ title, description, key });
            const nodeId = this.editor.addNode(
                key,
                1,
                1,
                pos_x,
                pos_y,
                key,
                { nodedata },
                "stepnode",
                "vue",
            );
            this.addedNodes.add(node.key);
            this.addToDrawflowLog(
                `Added node: ${node.key} at (${pos_x}, ${pos_y}) as id ${nodeId}`,
                "info",
            );
        },
        async addNodeToVuex(node, pos_x, pos_y) {
            // add node to vuex
            const stepPayload = structuredClone(node);
            delete stepPayload["updated_at"];
            delete stepPayload["created_at"];
            stepPayload["pos_x"] = pos_x;
            stepPayload["pos_y"] = pos_y;

            await this.saveJourneyStep(stepPayload);
            this.addToDrawflowLog(
                `Add step: ${stepPayload["key"]} to journey step.`,
                "info",
            );
        },
        createAllConnections() {
            this.addToDrawflowLog(
                "Starting to create all connections...",
                "bold",
            );
            for (const [key, node] of Object.entries(this.nodesData)) {
                this.addToDrawflowLog(`Processing node: ${key}`, "info");
                if (this.addedNodes.has(key)) {
                    this.addToDrawflowLog(
                        `Node ${key} has been added, processing conditions...`,
                        "info",
                    );
                    node.conditions.forEach((condition) => {
                        this.addToDrawflowLog(
                            `Processing condition: ${JSON.stringify(
                                condition,
                            )}`,
                            "info",
                        );
                        if (condition && condition.step) {
                            this.addToDrawflowLog(
                                `Condition step is ${condition.step}`,
                                "info",
                            );
                            if (this.addedNodes.has(condition.step)) {
                                this.addToDrawflowLog(
                                    `Target node ${condition.step} is added, creating connection...`,
                                    "info",
                                );
                                this.createConnection(key, condition);
                            } else {
                                this.addToDrawflowLog(
                                    `Target node ${condition.step} not added yet, skipping connection.`,
                                    "warning",
                                );
                            }
                        } else {
                            this.addToDrawflowLog(
                                `Condition step is missing or invalid for node ${key}`,
                                "warning",
                            );
                        }

                        // Handle nested conditions
                        if (condition.conditions) {
                            condition.conditions.forEach((nestedCondition) => {
                                this.addToDrawflowLog(
                                    `Processing nested condition: ${JSON.stringify(
                                        nestedCondition,
                                    )}`,
                                    "info",
                                );
                                if (nestedCondition && nestedCondition.step) {
                                    this.addToDrawflowLog(
                                        `Nested condition step is ${nestedCondition.step}`,
                                        "info",
                                    );
                                    if (
                                        this.addedNodes.has(
                                            nestedCondition.step,
                                        )
                                    ) {
                                        this.addToDrawflowLog(
                                            `Target node ${nestedCondition.step} is added, creating connection...`,
                                            "info",
                                        );
                                        this.createConnection(
                                            key,
                                            nestedCondition,
                                        );
                                    } else {
                                        this.addToDrawflowLog(
                                            `Target node ${nestedCondition.step} not added yet, skipping connection.`,
                                            "warning",
                                        );
                                    }
                                } else {
                                    this.addToDrawflowLog(
                                        `Nested condition step is missing or invalid for node ${key}`,
                                        "warning",
                                    );
                                }
                            });
                        }
                    });
                } else {
                    this.addToDrawflowLog(
                        `Node ${key} has not been added, skipping...`,
                        "warning",
                    );
                }
            }
            this.addToDrawflowLog("Finished creating all connections.", "bold");
        },
        createConnection(sourceKey, condition) {
            const targetKey = condition.step;
            this.addToDrawflowLog(
                `Attempting to create connection from ${sourceKey} to ${targetKey}`,
                "info",
            );

            if (
                targetKey &&
                this.nodesData[targetKey] &&
                this.addedNodes.has(sourceKey) &&
                this.addedNodes.has(targetKey)
            ) {
                try {
                    this.addToDrawflowLog(`Source Key, ${sourceKey}`, "info");
                    this.addToDrawflowLog(`Source Key, ${targetKey}`, "info");

                    const sourceNodes = this.editor.getNodesFromName(sourceKey);
                    const targetNodes = this.editor.getNodesFromName(targetKey);

                    if (sourceNodes.length && targetNodes.length) {
                        const sourceNode = sourceNodes[0];
                        const targetNode = targetNodes[0];

                        if (sourceNode && targetNode) {
                            this.editor.addConnection(
                                sourceNode,
                                targetNode,
                                "output_1",
                                "input_1",
                            );
                            this.addToDrawflowLog(
                                `Connection created from ${sourceKey} to ${targetKey}`,
                                "info",
                            );

                            // // Recalculate order
                            // this.updateOrderAfterConnection(
                            //     sourceKey,
                            //     targetKey,
                            // );
                        } else {
                            this.addToDrawflowLog(
                                `One of the nodes is missing: ${sourceKey} or ${targetKey}`,
                                "warning",
                            );
                        }
                    } else {
                        this.addToDrawflowLog(
                            `Nodes not found by name: ${sourceKey} or ${targetKey}`,
                            "warning",
                        );
                    }
                } catch (error) {
                    console.error(
                        `Failed to create connection from ${sourceKey} to ${targetKey}:`,
                        error,
                    );
                    this.addToDrawflowLog(
                        `Failed to create connection from ${sourceKey} to ${targetKey}`,
                        "warning",
                    );
                }
            } else {
                this.addToDrawflowLog(
                    `Invalid connection parameters for ${sourceKey} to ${targetKey}`,
                    "warning",
                );
            }
        },
        recalculateAllOrders() {
            this.addToDrawflowLog(
                "Starting order recalculation for all nodes...",
                "bold",
            );

            // Initialize global maxOrder
            this.maxOrder = 0;

            // Initialize a set to track processed nodes
            this.processedNodes = new Set();

            // Identify all root nodes (nodes without inputs via conditions.*.step)
            const rootNodes = Object.keys(this.nodesData).filter((nodeKey) => {
                const step = this.journeyStep(nodeKey);
                const hasParents = (Object.values(this.nodesData) || []).some(
                    (parentStep) =>
                        (parentStep.conditions || []).some(
                            (condition) => condition.step === nodeKey,
                        ),
                );
                return !hasParents; // True if no parent references this node
            });

            this.addToDrawflowLog(
                `Root nodes identified: ${rootNodes.join(", ")}`,
                "info",
            );

            // Start recalculating orders from each root node
            rootNodes.forEach((rootNodeKey) => {
                this.calculateOrderForNodeAndDescendants(rootNodeKey);
            });

            this.addToDrawflowLog(
                "All node orders recalculated successfully.",
                "bold",
            );
        },

        calculateOrderForNodeAndDescendants(nodeKey) {
            this.addToDrawflowLog(
                `Starting order calculation for node: ${nodeKey}`,
                "info",
            );

            const node = this.editor.getNodesFromName(nodeKey)?.[0];
            if (!node) {
                this.addToDrawflowLog(
                    `Node not found during order calculation: ${nodeKey}`,
                    "warning",
                );
                return;
            }

            if (this.parentProcessed.has(nodeKey)) {
                this.addToDrawflowLog(
                    `Node ${nodeKey} already processed as parent. Skipping.`,
                    "info",
                );
                return;
            }

            this.addToDrawflowLog(
                `Processing node: ${nodeKey} as parent`,
                "info",
            );

            // Update the current node's order
            const step = this.journeyStep(nodeKey);
            if (step) {
                // this.addToDrawflowLog(
                //     `Step metadata found for ${nodeKey}: ${JSON.stringify(
                //         step,
                //     )}`,
                //     "info",
                // );
                const newOrder = ++this.maxOrder; // Use and increment the global maxOrder
                // if (step.order !== newOrder) {
                this.addToDrawflowLog(
                      `Order for node ${nodeKey} : ${step.order}`,
                      "warning",
                  );
                if (!step.order) {
                    step.order = newOrder;
                    this.saveJourneyStep(step);

                    this.addToDrawflowLog(
                        `Updated order for node ${nodeKey} to ${newOrder}`,
                        "warning",
                    );
                } else {
                    this.addToDrawflowLog(
                        `Node ${nodeKey} already has the correct order: ${step.order}`,
                        "info",
                    );
                }
            } else {
                this.addToDrawflowLog(
                    `No step metadata found for node ${nodeKey}. Skipping children and siblings.`,
                    "warning",
                );
                return;
            }

            // Log conditions for debugging
            this.addToDrawflowLog(
                `Conditions for node ${nodeKey}: ${JSON.stringify(
                    step.conditions || [],
                )}`,
                "info",
            );

            // Step 1: Get all child nodes (vertical traversal)
            const childrenKeys = this.journeySteps
                .filter((step) => {
                    // this.addToDrawflowLog(
                    //     `Checking if ${step.key} is a child of ${nodeKey}`,
                    //     "info",
                    // );
                    return step.parent === nodeKey; // Check parent relationship
                })
                .map((child) => child.key); // Extract the step key for the child nodes

            if (childrenKeys.length > 0) {
                  this.addToDrawflowLog(
                    `Children for node ${nodeKey} based on parent relationship: ${JSON.stringify(
                        childrenKeys,
                    )}`,
                    "warning",
                );
                this.traverseChildren(nodeKey, childrenKeys);
            } else {
                this.addToDrawflowLog(
                    `Node ${nodeKey} has no children based on parent relationship`,
                    "info",
                );
            }

            // Mark this node as processed as a parent
            this.parentProcessed.add(nodeKey);

            // Step 3: Get sibling nodes (horizontal traversal)
            let siblingKeys = Array.from(
                new Set(
                    (step?.conditions || [])
                        .map((condition) => condition.step) // Extract steps from conditions
                        .filter((siblingStep) => {
                            // this.addToDrawflowLog(
                            //     `Checking if ${siblingStep} is a sibling of ${nodeKey}`,
                            //     "info",
                            // );
                            // Exclude direct children (already processed) and the current node
                            return (
                                !this.childrenProcessed.has(siblingStep) &&
                                siblingStep !== nodeKey
                            );
                        }),
                ),
            );

            this.addToDrawflowLog(
                `Siblings for node ${nodeKey} after filtering children: ${JSON.stringify(
                    siblingKeys,
                )}`,
                "warning",
            );

            // Step 4: Traverse siblings horizontally
            if (siblingKeys.length > 0) {
                this.traverseSiblings(siblingKeys);
            }
        },
        traverseChildren(parentKey, childrenKeys) {
            childrenKeys.forEach((childNodeKey) => {
                if (!this.childrenProcessed.has(childNodeKey)) {
                    this.addToDrawflowLog(
                        `Processing child node: ${childNodeKey} from parent node: ${parentKey}`,
                        "info",
                    );

                    const childStep = this.journeyStep(childNodeKey);
                    if (childStep) {
                        if (!childStep.order) {
                            const newOrder = ++this.maxOrder;
                        //if (childStep.order !== newOrder) {
                            childStep.order = newOrder;
                            this.saveJourneyStep(childStep);

                            this.addToDrawflowLog(
                                `Updated order for ${childNodeKey} to ${newOrder}`,
                                "info",
                            );
                        }
                    }

                    // Mark the child node as processed
                    this.childrenProcessed.add(childNodeKey);

                    // // Process downstream nodes of this child
                    // const downstreamSteps = (childStep?.conditions || [])
                    //     .map((condition) => condition.step)
                    //     .filter(Boolean);

                    // if (downstreamSteps.length > 0) {
                    //     this.traverseChildren(childNodeKey, downstreamSteps);
                    // }


                    // Perform the full recursive calculation for the sibling
                    this.calculateOrderForNodeAndDescendants(childNodeKey);
                }
            });
        },

        traverseSiblings(siblingKeys) {
            siblingKeys.forEach((siblingNodeKey) => {
                if (!this.parentProcessed.has(siblingNodeKey)) {
                    this.addToDrawflowLog(
                        `Processing sibling node: ${siblingNodeKey}`,
                        "info",
                    );

                    // Perform the full recursive calculation for the sibling
                    this.calculateOrderForNodeAndDescendants(siblingNodeKey);

                    // Mark the sibling node as processed
                    this.parentProcessed.add(siblingNodeKey);
                } else {
                    this.addToDrawflowLog(
                        `Sibling node ${siblingNodeKey} already processed. Skipping.`,
                        "info",
                    );
                }
            });
        },

        allowDrop(ev) {
            ev.preventDefault();
        },
        drag(ev) {
            ev.dataTransfer.setData(
                "node",
                ev.target.getAttribute("data-node"),
            );
        },
        async drop(ev) {
            ev.preventDefault();
            const data = ev.dataTransfer.getData("node");
            const pos_x = this.calculateX(ev.clientX);
            const pos_y = this.calculateY(ev.clientY);

            this.$modal.show(WaitingDialog, {
                title: "Adding journey step...",
                message: "Please wait while we are adding journey step...",
            });
            try {
                if (
                    this.groupedSidebarNodes.some(
                        (group) => group.parent.key === data,
                    )
                ) {
                    const group = this.groupedSidebarNodes.find(
                        (group) => group.parent.key === data,
                    );
                    const parentNode = this.sidebarNodesData[group.parent.key];

                    if (!this.addedNodes.has(group.parent.key)) {
                        await this.addNodeToVuex(parentNode, pos_x, pos_y);
                        this.addNodeToDrawFlow(parentNode, pos_x, pos_y);
                        this.removeNodeFromSidebar(group.parent.key);

                        await group.children.forEach((child, index) => {
                            const childPosY = pos_y + (index + 1) * 200;
                            this.addNodeToVuex(child, pos_x, pos_y);
                            this.addNodeToDrawFlow(child, pos_x, childPosY);
                            this.removeNodeFromSidebar(child.key);
                        });
                        this.createAllConnections(); // Ensure connections are created after nodes are added
                    }
                } else {
                    const node = this.sidebarNodesData[data];
                    if (!this.addedNodes.has(data)) {
                        await this.addNodeToVuex(node, pos_x, pos_y);
                        this.addNodeToDrawFlow(node, pos_x, pos_y);
                        this.removeNodeFromSidebar(data);
                        this.createAllConnections(); // Ensure connections are created after nodes are added
                    }
                }
                this.$modal.submit();
                this.$modal.show(ConfirmDialog, {
                    title: "Journey Step Added Successfully",
                    message:
                        "The journey step has been added successfully. You can now edit it by clicking on edit button.",
                    confirmText: "Confirm",
                });
            } catch (error) {
                this.$modal.submit();
                this.$modal.show(ConfirmDialog, {
                    title: "Failed",
                    message: error,
                    confirmText: "Confirm",
                });
            }
        },
        calculateX(pos_x) {
            // Calculate the scale factor
            const scaleFactor =
                this.editor.precanvas.clientWidth /
                (this.editor.precanvas.clientWidth * this.editor.zoom);
            // Get the X-coordinate of the precanvas
            const precanvasX = this.editor.precanvas.getBoundingClientRect().x;
            // Calculate the scaled mouse X position
            const scaledMouseX = pos_x * scaleFactor;
            // Calculate the scaled precanvas X position
            const scaledPrecanvasX = precanvasX * scaleFactor;
            // Calculate the final result
            const result = scaledMouseX - scaledPrecanvasX;
            // return result
            return result;
        },
        calculateY(pos_y) {
            // Calculate the scale factor
            const scaleFactor =
                this.editor.precanvas.clientHeight /
                (this.editor.precanvas.clientHeight * this.editor.zoom);
            // Get the X-coordinate of the precanvas
            const precanvasY = this.editor.precanvas.getBoundingClientRect().y;
            // Calculate the scaled mouse X position
            const scaledMouseY = pos_y * scaleFactor;
            // Calculate the scaled precanvas X position
            const scaledPrecanvasY = precanvasY * scaleFactor;
            // Calculate the final result
            const result = scaledMouseY - scaledPrecanvasY;
            // return result
            return result;
        },
        removeNodeFromSidebar(key) {
            this.sidebarNodes = this.sidebarNodes.filter(
                (node) => node.key !== key,
            );
            this.groupedSidebarNodes = this.groupedSidebarNodes
                .map((group) => {
                    return {
                        parent: group.parent,
                        children: group.children.filter(
                            (child) => child.key !== key,
                        ),
                    };
                })
                .filter((group) => group.children.length > 0);
        },
        updateLastPosition() {
            this.lastPosition.x += 250; // Adjust horizontal spacing as needed
            this.lastPosition.y = 100; // Keep y position constant for single row
        },
        exportData() {
            console.log(this.editor.export());
        },
        toggleLock() {
            const isFixed = this.editor.editor_mode === "fixed";
            this.editor.editor_mode = isFixed ? "edit" : "fixed";
            this.isLocked = !isFixed;
        },
        openConnectionConditionDialog(outputNodeKey, inputNodeKey) {
            this.connectionConditionDialog.show = true;
            this.connectionConditionDialog.title = `Transition condition from ${outputNodeKey} to ${inputNodeKey}`;
            this.connectionConditionDialog.data = {
                path: null,
                comparison: "==",
                value: null,
                step: inputNodeKey,
            };
        },
        async saveTransitionConditions(newCondition) {
            const updatedJourneyStep = { ...this.outputJourneyStep };
            updatedJourneyStep.conditions.push({ ...newCondition });
            try {
                this.connectionConditionDialog.show = false;
                this.$modal.show(WaitingDialog, {
                    title: "Saving...",
                    message:
                        "Please wait while saving the trasition condition...",
                });
                await this.saveJourneyStep(updatedJourneyStep);
                this.$modal.submit();
                this.$modal.show(ConfirmDialog, {
                    title: "Transition Condition Added Successfully",
                    message: `The transition condition from ${updatedJourneyStep.key} to ${newCondition.step} has been saved successfully.`,
                    confirmText: "Confirm",
                });
            } catch (error) {
                this.$modal.submit();
                this.$modal.show(ConfirmDialog, {
                    title: "Failed",
                    message: error,
                    confirmText: "Confirm",
                });
            }
            this.connectionConditionDialog.data = {
                path: null,
                comparison: "==",
                value: null,
                step: null,
            };
            this.outputNodeKey = null;
        },
        addToDrawflowLog(message, type) {
            switch (type) {
                case "warning":
                    this.drawflowLogs.push(
                        `<span style="color:red">${message}</span>`,
                    );
                    break;
                case "bold":
                    this.drawflowLogs.push(
                        `<span style="font-weight:bold">${message}</span>`,
                    );
                    break;
                case "info":
                default:
                    this.drawflowLogs.push(`<span>${message}</span>`);
                    break;
            }
        },
        showDrawflowLog() {
            this.showDrawflowLogDialog = true;
            this.setNeedToRefreshCanvas(true);
        },
        async saveAllJourneySteps() {
            try {
                const result = await this.saveJourneySteps();
                if (result) {
                    this.isDirty = false;
                    this.$modal.show(ConfirmDialog, {
                        title: "Journey Steps Saved Successfully",
                        message: "Your changes have been saved successfully!",
                        confirmText: "Confirm",
                    });
                } else {
                    this.$modal.show(ConfirmDialog, {
                        title: "Failed for Saving Journey Steps",
                        message: "Please create journey before save the steps.",
                        confirmText: "Confirm",
                    });
                }
            } catch (error) {
                this.$modal.show(ConfirmDialog, {
                    title: "Failed",
                    message: error,
                    confirmText: "Confirm",
                });
            }
        },
        updateOrderForNode(nodeKey, newOrder) {
            const node = this.journeyStep(nodeKey);

            if (!node) {
                this.addToDrawflowLog(
                    `Node with key ${nodeKey} not found.`,
                    "warning",
                );
                return false;
            }

            if (node.order !== newOrder) {
                node.order = newOrder;
                this.addCurrentJourneySteps({ key: nodeKey, step: node }); // Update in Vuex
                this.addToDrawflowLog(
                    `Updated order for node ${nodeKey} to ${newOrder}`,
                    "info",
                );
                return true; // Mark this node for saving
            }

            return false; // No changes made
        },

        updateOrdersForConnections(outputNodeKey) {
            const outputNode = this.editor.getNodesFromName(outputNodeKey)[0];

            if (!outputNode) {
                this.addToDrawflowLog(
                    `Node with key ${outputNodeKey} not found in editor.`,
                    "warning",
                );
                return;
            }

            const connections = outputNode.outputs?.output_1?.connections || [];
            connections.forEach(({ node: inputNodeId }) => {
                const inputNode = this.editor.getNodeFromId(inputNodeId);
                if (inputNode) {
                    const inputNodeKey = inputNode.name;
                    const newOrder =
                        this.journeyStep(outputNodeKey)?.order + 1 || 1;
                    const shouldSave = this.updateOrderForNode(
                        inputNodeKey,
                        newOrder,
                    );

                    if (shouldSave) {
                        this.saveJourneyStep(this.journeyStep(inputNodeKey)); // Save only updated node
                    }
                }
            });
        },

        refreshCanvas() {
            this.nodesData = {};
            this.sidebarNodesData = {};
            this.sidebarNodes = [];
            this.groupedSidebarNodes = [];
            (this.lastPosition = { x: 100, y: 100 }),
                (this.addedNodes = new Set());
            this.editor.clearModuleSelected();
            this.loadNodesFromJson();
            this.addAllNodes();
            this.loadSidebarNodesData();
            this.loadSideNodes();
            setTimeout(() => {
                this.createAllConnections();
            });
        },
    },
    mounted() {
        this.initializeEditor();
        this.loadNodesFromJson();
        this.addAllNodes();
        this.loadSidebarNodesData();
        this.loadSideNodes();
        setTimeout(() => {
            this.createAllConnections();
            this.recalculateAllOrders();
        });
        this.unwatchDeleteNode = this.$store.watch(
            (state, getters) => getters["journey/needToRefreshCanvas"],
            (newValue, oldValue) => {
                if (newValue) {
                    this.refreshCanvas();
                    this.setNeedToRefreshCanvas(false);
                }
            },
        );
    },
    beforeDestroy() {
        this.unwatchDeleteNode();
    },
};
</script>

<style lang="scss" src="./FlowManagement.scss"></style>

<style lang="scss" scoped>
@import "@/assets/scss/theme.scss";
.primary-btn {
    color: white;
    background-color: $ausloans-blue !important;
}

.danger-btn {
    color: white;
    background-color: $ausloans-red !important;
}

/* Layout container for sidebar and editor */
.editor-layout {
    display: flex;
    height: 100%;
    overflow: hidden; /* Prevents overflow */
}

/* Sidebar Drawer */
.sidebar-drawer {
    width: 250px; /* Set fixed width */
    background: #f5f5f5;
    height: 100%;
    position: relative; /* Keep it inside container */
    z-index: 1;
}

/* Main Editor Area */
.editor-content {
    flex-grow: 1;
    overflow: auto; /* Allow scrolling within editor */
    padding: 20px;
    background: white;
}

/* Sidebar Toggle Button */
.toggle-btn {
    position: absolute;
    top: 10px;
    left: 10px;
    z-index: 10;
    background: white;
    border-radius: 50%;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
}
</style>
